import React from 'react'
import { CornerDownRight, Layers } from 'lucide-react'
import { cn } from '@/lib/utils'

interface RoomTypeTreeRowProps {
  name: string
  depth?: number
  isParent?: boolean
  childCount?: number
  isLastChild?: boolean
  selected?: boolean
  /**
   * This room follows a parent in ANOTHER hotel (Groups). Such a child is not indented — it is
   * sorted with its own hotel — so the relationship is stated in words instead of drawn as a line
   * across a hotel boundary. Null/absent for every same-hotel and ungrouped room.
   */
  followsName?: string | null
  followsPropertyName?: string | null
}

/**
 * Label portion of a room-type option: a tree connector for linked children,
 * a bold name + count badge for parents, plain text for standalone rooms.
 * Rendered after the dropdown's own selection control. The enclosing option
 * must be a `group` and use `items-stretch` so the connector spans the row.
 */
export default function RoomTypeTreeRow({
  name,
  depth = 0,
  isParent = false,
  childCount = 0,
  isLastChild = false,
  selected = false,
  followsName,
  followsPropertyName,
}: RoomTypeTreeRowProps) {
  const isChild = depth > 0

  return (
    <span className="flex min-w-0 flex-1 items-stretch">
      {isChild && (
        <span className="relative mr-1.5 w-4 shrink-0" aria-hidden="true">
          {isLastChild ? (
            <span className="absolute left-1.5 -top-1.5 bottom-1/2 w-2.5 rounded-bl-[7px] border-b border-l border-border/70" />
          ) : (
            <>
              <span className="absolute left-1.5 -top-1.5 -bottom-1.5 w-px bg-border/70" />
              <span className="absolute left-1.5 top-1/2 h-px w-2.5 bg-border/70" />
            </>
          )}
        </span>
      )}

      <span className="flex min-w-0 flex-1 flex-col justify-center">
        <span className="flex min-w-0 items-center gap-2">
          <span
            className={cn(
              'truncate',
              isChild
                ? cn(
                    'text-muted-foreground transition-colors group-hover:text-foreground',
                    selected && 'text-foreground'
                  )
                : isParent
                  ? 'font-semibold text-foreground'
                  : 'text-foreground'
            )}
          >
            {name}
          </span>

          {isParent && (
            <span className="ml-auto inline-flex shrink-0 items-center gap-1 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-primary">
              <Layers className="h-2.5 w-2.5" />
              {childCount}
            </span>
          )}
        </span>

        {followsName && (
          <span className="mt-0.5 flex min-w-0 items-center gap-1 text-[10px] text-primary">
            <CornerDownRight className="h-2.5 w-2.5 shrink-0" />
            <span className="truncate">
              Follows {followsName}
              {followsPropertyName ? ` at ${followsPropertyName}` : ''}
            </span>
          </span>
        )}
      </span>
    </span>
  )
}
